import * as React from 'react';
import { useHistory } from 'react-router-dom';

import * as Yup from 'yup';

import Button from '@common/react/components/Forms/Button';
import {
	dateValidator,
	lengthValidator,
	optionalEmailValidator,
	phoneRegexp,
	phoneValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import ScrollToError from '@common/react/components/Forms/ScrollToError';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import ChangeEmailModal from '@common/react/components/UI/ChangeEmailModal/ChangeEmailModal';
import ChangePhoneModal from '@common/react/components/UI/ChangePhoneModal/ChangePhoneModal';

import { Gender } from '@commonTuna/react/objects/Enums';
import { BaseInvite } from '@commonTuna/react/objects/BaseInvite';
import { PortalRegistrationStep } from '@commonTuna/react/objects/PortalRegistrationStep';

import { RegistrationUser, User, UserRole } from '@app/objects/User';
import { Suffix } from '@app/objects/Suffix';
import { MarriageStatus } from '@app/objects/MarriageStatus';
import DemographicInfoRegistration from '@app/components/UI/DemographicInfo/DemographicInfoRegistration';
import { PatientLocation } from '@app/objects/PatientLocation';
import { PortalSettings } from '@app/components/Pages/Register/Register';

type FormValues = RegistrationUser | User;

interface ComponentProps {
	onSubmit: (response: UserRegistrationSteps) => void;
}

export interface UserRegistrationSteps {
	steps: Array<PortalRegistrationStep>;
	hippa: Array<BaseInvite>;
	forms: Array<BaseInvite>;
	user: User;
	portalSettings: PortalSettings;
}

const validationSchema = Yup.object().shape({
	firstName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	lastName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	birthDate: dateValidator,
	// city: simpleStringValidator,
	// region: simpleStringValidator,
	// zip: simpleStringValidator,
	password: simpleStringValidator.min(8).required(),
	repeatPassword: Yup.string().required().oneOf([Yup.ref('password')], 'Repeat password should be the same as password'),
	phoneNumber: Yup.string()
		.when('email', {
			is: (v) => !v,
			then: Yup.string().matches(phoneRegexp, 'Invalid phone number').required('Cell Phone or Email is required'),
			otherwise: phoneValidator,
		}),
	email: Yup.string()
		.when('phoneNumber', {
			is: (v) => !(v),
			then: Yup.string().email().required('Cell Phone or Email is required'),
			otherwise: optionalEmailValidator,
		}),
}, [['email', 'phoneNumber']]);

export const PatientMainForm: React.FC<ComponentProps> = ({ onSubmit }) => {
	const [loading, setLoading] = React.useState(false);
	const history = useHistory();
	const data = history.location?.state?.data;
	const [initialErrors, setInitErrors] = React.useState({});

	const initialValues = {
		role: UserRole.User,
		suffix: Suffix.Mr,
		firstName: data?.firstName || '',
		lastName: data?.lastName || '',
		gender: data?.gender || Gender.Male,
		birthDate: data?.birthDate || null,
		marriageStatus: MarriageStatus.Single,
		email: data?.email || '',
		phoneNumber: data?.phone || '',
		password: '',
		repeatPassword: '',
		address: '',
		city: '',
		region: '',
		zip: '',
		emergencyContactName: '',
		emergencyContactPhone: '',
		emergencyContactRelation: '',
		allowEmail: true,
		allowSms: true,
		allowCalls: true,
		allowPush: true,
		agreementForSignUp: true,
		captcha: '',
		patientLocations: [] as Array<PatientLocation>,
	} as RegistrationUser;
	React.useEffect(() => {
		validationSchema.validate(initialValues, { abortEarly: false })
			.catch((err) => {
				const er = {};
				for (let i = 0; i < err.inner.length; i++) {
					er[err.inner[i].path] = err.inner[i].errors[0];
				}
				setInitErrors(er);
			});
	}, []);

	const renderButtons = () => {
		return <>
			<div className="text-center button-box">
				<Button
					isLoading={loading}
					className="btn btn-component mb10"
				>
					Create Account
				</Button>
			</div>
		</>;
	};

	return (
		<>
			<div className="patient-register-page__form">
				<ItemProvider<FormValues>
					id={-1}
					type="userRegistrationAuth"
					readonly={false}
					add={initialValues}
					validationSchema={validationSchema}
				>
					<ItemEditor<FormValues>
						resetFormAfterSubmit
						beforeSubmit={(values, actions, submit) => {
							setLoading(true);
							submit()
								.finally(() => setLoading(false));
							return false;
						}}
						formikProps={{
							validateOnMount: true,
							initialErrors,
						}}
						afterSubmit={(_, res) => onSubmit(res)}
						edit={(formikBag) => <>
							<ScrollToError {...formikBag} formId="patient-registration-form" />
							<DemographicInfoRegistration formikBag={formikBag} />
							{renderButtons()}
						</>}
					/>
				</ItemProvider>
			</div>
		</>
	);
};
